import React from 'react';
import { TwitterIcon, GithubIcon, CopilotIcon, SparklesIcon, CheckIcon } from './Icons';

interface FooterProps {
    onOpenChangelog: () => void;
    navigateTo: (path: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onOpenChangelog, navigateTo }) => {
    const currentYear = new Date().getFullYear();

    const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        e.preventDefault();
        navigateTo(path);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const sitePageIds = ['about-us', 'contact-us', 'privacy-policy', 'terms-of-service', 'sitemap'];

    const footerColumns = [
        {
            title: 'PDF & Documents',
            links: [
                { label: 'Compress PDF', toolId: 'pdf-compress' },
                { label: 'Merge PDF', toolId: 'pdf-merge' },
                { label: 'Split PDF', toolId: 'pdf-split' },
                { label: 'PDF to Image', toolId: 'pdf-to-image' },
                { label: 'Protect PDF', toolId: 'pdf-security' },
                { label: 'Unlock PDF', toolId: 'pdf-unlock' },
            ]
        },
        {
            title: 'Media & Creative',
            links: [
                { label: 'Image Editor Studio', toolId: 'image-editor' },
                { label: 'Polaroid Photo Maker', toolId: 'polaroid-maker' },
                { label: 'Image Format Converter', toolId: 'image-format-converter' },
                { label: 'Viral Meme Generator', toolId: 'meme-generator' },
                { label: 'YouTube Tag Extractor', toolId: 'youtube-tag-extractor' },
                { label: '4K Thumbnail Grabber', toolId: 'youtube-thumbnail-downloader' },
            ]
        },
        {
            title: 'Calculators & Dev',
            links: [
                { label: 'Loan EMI Calculator', toolId: 'loan-calculator' },
                { label: 'Age Calculator', toolId: 'age-calculator' },
                { label: 'Currency Converter', toolId: 'currency' },
                { label: 'JSON Formatter', toolId: 'json-formatter' },
                { label: 'Random Password Gen', toolId: 'random-password-generator' },
                { label: 'IP Location Finder', toolId: 'ip-location-finder' },
            ]
        },
        {
            title: 'Company & Legal',
            links: [
                { label: 'About ToolsCopilot', toolId: 'about-us' },
                { label: 'Blog & Deep Guides', href: '/blog' },
                { label: 'Contact Support', toolId: 'contact-us' },
                { label: 'Privacy Policy', toolId: 'privacy-policy' },
                { label: 'Terms of Service', toolId: 'terms-of-service' },
                { label: 'HTML Sitemap', toolId: 'sitemap' },
            ]
        }
    ];

    return (
        <footer className="bg-slate-100 dark:bg-slate-950 text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800/80 transition-colors duration-300 relative overflow-hidden">
            {/* Ambient Background Mesh */}
            <div className="absolute top-0 left-1/4 w-96 h-32 bg-cyan-500/10 dark:bg-cyan-500/5 blur-3xl pointer-events-none -z-0" />
            <div className="absolute top-0 right-1/4 w-96 h-32 bg-blue-500/10 dark:bg-blue-500/5 blur-3xl pointer-events-none -z-0" />

            <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 relative z-10">
                {/* Top Promo Banner inside Footer */}
                <div className="mb-14 p-6 sm:p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-lg dark:shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6 text-left transition-colors">
                    <div className="space-y-1.5">
                        <div className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> Client-Side Innovation
                        </div>
                        <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                            Zero Cloud Uploads. 100% In-Browser Privacy.
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm max-w-xl leading-relaxed">
                            ToolsCopilot processes your sensitive documents, PDFs, media, and calculations entirely within local browser memory using modern WebAssembly.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                            onClick={scrollToTop}
                            className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs sm:text-sm shadow-md shadow-cyan-600/20 transition-all hover:scale-105 flex items-center gap-2 cursor-pointer"
                        >
                            <span>Explore 140+ Tools</span>
                            <span>↑</span>
                        </button>
                    </div>
                </div>

                {/* 5-Column Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 text-left">
                    {/* Brand Column (Span 2) */}
                    <div className="lg:col-span-2 space-y-4">
                        <a 
                            href="/" 
                            onClick={(e) => handleNavigate(e, '/')} 
                            className="inline-flex items-center gap-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white group"
                        >
                            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400 text-transparent bg-clip-text">Tools</span>
                            <div className="p-1 rounded-lg bg-cyan-500/10 dark:bg-cyan-500/20 border border-cyan-500/20 dark:border-cyan-400/40 text-cyan-600 dark:text-cyan-300 group-hover:scale-110 transition-transform">
                                <CopilotIcon className="w-5 h-5" />
                            </div>
                            <span className="bg-lime-400 text-slate-950 px-2 py-0.5 rounded-lg font-black text-lg">Copilot</span>
                        </a>

                        <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed max-w-sm">
                            The definitive all-in-one productivity suite. 140+ high-speed, client-side web tools for PDF optimization, image remastering, media extraction, and financial calculations.
                        </p>

                        {/* Live System Status Badge & Buy Me a Coffee */}
                        <div className="pt-2 flex flex-col gap-3">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 w-fit shadow-xs">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                                <span>All 140+ Systems Operational</span>
                            </div>

                            {/* Buy Me a Coffee Button */}
                            <a
                                href="https://buymeacoffee.com/shamsulhaqt"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-[#FFDD00] hover:bg-[#FFEA47] text-slate-950 font-black text-xs shadow-md shadow-amber-500/10 hover:shadow-amber-500/20 hover:scale-105 transition-all cursor-pointer border border-amber-300/80 w-fit group"
                            >
                                <span className="text-base group-hover:rotate-12 transition-transform">☕</span>
                                <span>Buy me a coffee</span>
                            </a>
                        </div>
                    </div>

                    {/* 4 Tool Link Columns */}
                    {footerColumns.map(column => (
                        <div key={column.title} className="space-y-4">
                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-2">
                                {column.title}
                            </h4>
                            <ul className="space-y-2.5">
                                {column.links.map(link => {
                                    const isSitePage = link.toolId && sitePageIds.includes(link.toolId);
                                    let path = link.href;
                                    if (link.toolId) {
                                        path = isSitePage ? `/${link.toolId}` : `/tool/${link.toolId}`;
                                    }

                                    return (
                                        <li key={link.label}>
                                            <a
                                                href={path || '#'}
                                                onClick={(e) => {
                                                    if (path === '/blog') {
                                                        handleNavigate(e, '/blog');
                                                    } else if (link.toolId) {
                                                        handleNavigate(e, path!);
                                                    }
                                                }}
                                                className="text-xs sm:text-sm text-slate-600 hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-400 transition-colors inline-block font-medium"
                                            >
                                                {link.label}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom Trust & Copyright Row */}
                <div className="mt-14 pt-8 border-t border-slate-200 dark:border-slate-800/80 flex flex-col md:flex-row justify-between items-center gap-4 text-left">
                    <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
                        <span>&copy; {currentYear} ToolsCopilot.com. All rights reserved.</span>
                        <span className="hidden sm:inline">•</span>
                        <span>Zero server file logging</span>
                        <span className="hidden sm:inline">•</span>
                        <span>100% Free Forever</span>
                    </div>

                    <div className="flex items-center space-x-5">
                        <button 
                            onClick={onOpenChangelog} 
                            className="text-xs font-bold text-slate-600 hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-400 transition-colors cursor-pointer"
                        >
                            Changelog / What's New
                        </button>
                        <div className="w-px h-3.5 bg-slate-200 dark:bg-slate-800" />

                        <a 
                            href="https://github.com/ToolsCopilot" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            aria-label="ToolsCopilot GitHub Repository"
                            className="text-slate-500 hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-400 transition-colors"
                        >
                            <GithubIcon className="w-4 h-4" />
                        </a>
                        <button
                            onClick={scrollToTop}
                            className="p-2 rounded-lg bg-slate-200/80 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-all text-xs font-bold cursor-pointer"
                            title="Scroll to top"
                        >
                            ↑ Top
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
};
